import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Scale, 
  ArrowLeft,
  FileText,
  Shield,
  User,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-pink-50 to-red-50">
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-pink-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Scale className="h-6 w-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                EduLegalHub
              </span>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
                <ArrowLeft className="h-4 w-4 mr-2 inline" />
                Back to Home
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <Badge className="bg-gradient-to-r from-blue-100 to-pink-100 text-blue-700 border-blue-200 px-4 py-2 mb-6">
              Legal Terms
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                Terms and Conditions
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
              Please read these terms and conditions carefully before using EduLegalHub.
              By using our platform, you agree to be bound by these terms.
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
              <span className="flex items-center">
                <FileText className="h-4 w-4 mr-1" />
                Last Updated: 01/10/2025
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="pb-20 px-4">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto space-y-8">

            {/* Introduction */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-blue-800">
                  <Scale className="h-6 w-6 mr-3" />
                  Welcome to EduLegalHub
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Welcome to EduLegalHub ("Platform"), an online subscription-based platform designed for two main purposes:
                </p>
                <div className="space-y-2 ml-4">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700"><strong>Legal Assistance</strong> – Providing simplified tutorials, explanations, and AI-assisted drafting of civil legal documents for non-lawyers in Ghana.</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700"><strong>Educational Resources</strong> – Providing lesson notes and related academic materials to teachers in Ghana.</span>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  By accessing or using EduLegalHub, you ("User", "You") agree to these Terms and Conditions. 
                  If you do not agree, you should not use the Platform.
                </p>
              </CardContent>
            </Card>

            {/* Section 1 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-gray-800">1. Purpose of EduLegalHub</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-800 mb-2">1.1 EduLegalHub aims to:</p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Simplify legal education for ordinary users through plain-English tutorials.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Assist in generating draft civil legal documents.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Provide teachers with structured lesson notes and teaching materials.</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-2">1.2</p>
                  <p className="text-gray-700">EduLegalHub is an educational and assistance platform only. It is not a substitute for licensed legal services or official educational curriculum bodies.</p>
                </div>
              </CardContent>
            </Card>

            {/* Section 2 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-gray-800">2. Eligibility</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-800 mb-2">2.1</p>
                  <p className="text-gray-700">You must be at least 18 years old to create an account.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-2">2.2</p>
                  <p className="text-gray-700">Teachers under 18 may only use the platform with the consent and supervision of a parent, guardian, or school authority.</p>
                </div>
              </CardContent>
            </Card>

            {/* Section 3 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-gray-800">
                  <User className="h-5 w-5 mr-2" />
                  3. User Responsibilities
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-800 mb-2">3.1 You agree to:</p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Provide accurate, truthful information when using the platform.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Use EduLegalHub for personal, educational, or institutional purposes only.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Not misuse the Platform for fraudulent, illegal, or harmful activities.</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-2">3.2 For Legal Document Services:</p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">You are responsible for the accuracy of any information entered into the document-generation forms.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">You acknowledge that generated documents are drafts and may require verification by a lawyer or Commissioner for Oaths.</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-2">3.3 For Teacher Lesson Notes:</p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">You may download and use the materials for personal teaching purposes.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">You may not sell, reshare, or distribute EduLegalHub lesson notes without written permission.</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Section 4 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-gray-800">4. Subscriptions and Payments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-800 mb-2">4.1</p>
                  <p className="text-gray-700">Certain services on EduLegalHub require a paid subscription. Subscription details will be made clear before purchase.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-2">4.2</p>
                  <p className="text-gray-700">Payments are non-refundable except where required by law.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-2">4.3</p>
                  <p className="text-gray-700">Unauthorized sharing of a subscription account or lesson notes will result in suspension or termination.</p>
                </div>
              </CardContent>
            </Card>

            {/* Section 5 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-gray-800">5. Intellectual Property</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-800 mb-2">5.1</p>
                  <p className="text-gray-700">All content on EduLegalHub — including legal tutorials, document templates, AI-generated outputs, and teacher lesson notes — is the intellectual property of EduLegalHub.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-2">5.2</p>
                  <p className="text-gray-700">Users are granted a limited, non-exclusive, non-transferable license to use downloaded materials for personal or classroom purposes only.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-2">5.3 You may not:</p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Reproduce, resell, or commercially exploit EduLegalHub content.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Remove watermarks, disclaimers, or copyright notices.</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Section 6 */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-gray-800">
                  <AlertTriangle className="h-5 w-5 mr-2 text-yellow-600" />
                  6. Disclaimer of Liability
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-800 mb-2">6.1 Legal Documents:</p>
                  <p className="text-gray-700">EduLegalHub does not provide professional legal advice. Documents generated are for educational and assistance purposes and may not always meet specific court requirements. Always consult a licensed lawyer when necessary.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-2">6.2 Lesson Notes:</p>
                  <p className="text-gray-700">EduLegalHub lesson notes are supplementary teaching resources. They are not an official replacement for materials from the Ghana Education Service (GES) or the Ministry of Education.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-2">6.3</p>
                  <p className="text-gray-700">The Platform is provided "as is" and "as available", without warranties of any kind.</p>
                </div>
              </CardContent>
            </Card>

            {/* Sections 7-11 */}
            <div className="grid md:grid-cols-1 gap-6">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center text-gray-800">
                    <Shield className="h-5 w-5 mr-2" />
                    7. Privacy and Data Protection
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p><span className="font-semibold">7.1</span> EduLegalHub collects personal and usage data to operate its services.</p>
                  <p><span className="font-semibold">7.2</span> Data will be processed in line with Ghana's Data Protection Act, 2012 (Act 843).</p>
                  <p><span className="font-semibold">7.3</span> EduLegalHub will not sell your data to third parties.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-gray-800">8. Limitation of Liability</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="font-semibold text-gray-800 mb-2">8.1 EduLegalHub, its owners, and affiliates are not liable for:</p>
                    <ul className="space-y-1 ml-4 text-gray-700">
                      <li>• Errors in generated documents or downloaded lesson notes.</li>
                      <li>• Rejection of documents by a court.</li>
                      <li>• Misuse of teaching resources.</li>
                      <li>• Losses, damages, or claims arising from use of the Platform.</li>
                    </ul>
                  </div>
                  <p><span className="font-semibold">8.2</span> Your only remedy if dissatisfied with the Platform is to stop using it.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-gray-800">9. Suspension and Termination</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p><span className="font-semibold">9.1</span> EduLegalHub may suspend or terminate your account if you breach these Terms.</p>
                  <p><span className="font-semibold">9.2</span> EduLegalHub may limit or discontinue services for technical, maintenance, or regulatory reasons.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-gray-800">10. Amendments</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p><span className="font-semibold">10.1</span> EduLegalHub reserves the right to modify these Terms at any time.</p>
                  <p><span className="font-semibold">10.2</span> Continued use of the Platform after updates constitutes acceptance.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-gray-800">11. Governing Law and Jurisdiction</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p><span className="font-semibold">11.1</span> These Terms shall be governed by the laws of Ghana.</p>
                  <p><span className="font-semibold">11.2</span> Any disputes shall be resolved exclusively in the courts of Ghana.</p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Section */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-600 to-pink-600 text-white">
              <CardContent className="p-8 text-center">
                <h3 className="text-xl font-semibold mb-4">Questions About These Terms?</h3>
                <p className="mb-6">If you have any questions about these Terms and Conditions, please contact us.</p>
                <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-full font-semibold hover:bg-gray-100 transition-colors">
                  Contact Us
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsAndConditions;